/**
 * crypto.ts
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
 */
"use strict";

import { DateTime } from "luxon";
import moment from "moment-timezone";

import { RatchetTreeNode, Group, GroupType, GroupSubmissionType, GroupPKI,
  ProtocolScheme } from "./group";

export enum ProtocolVersion {
  reserved = 0,
  mls10 = 1,
  scMLS1 = 100
}

export enum ProposalType {
  reserved = 0,
  add = 1,
  update = 2,
  remove = 3,
  psk = 4,
  reinit = 5,
  external_init = 6,
  app_ack = 7
}

export interface KeyPackage {
  version: ProtocolVersion
}

export interface TreePaths {
  path: Array<RatchetTreeNode>,
  copath: Array<RatchetTreeNode>
}

export interface UpdatePathNode {
  publicKey: string,
  verifyKey?: string,
  index: number,
  encPathSecret?: string,
  signingIndex: number
}

export class RatchetTree implements Group {
  _id: string;
  accountID: string;
  groupname: string;
  grouptype: GroupType;
  submissiontype: GroupSubmissionType;
  desc: string;
  archiving: boolean;
  pki: GroupPKI;
  protocolScheme: ProtocolScheme;
  protoVersion: number;
  userHash?: string;

  created: moment.Moment | Date;

  epoch: number;
  msgCounter: number;
  //treeHash: string;
  //confirmedTranscriptHash: string;
  //interimTranscriptHash: string;

  sender_data_secret?: Uint8Array;
  exporter_secret?: Uint8Array;
  authentication_secret?: Uint8Array;
  //external_secret?: Uint8Array;
  confirmation_key?: Uint8Array;
  membership_key?: Uint8Array;
  next_init_secret?: Uint8Array;

  resumption_secrets: Array<{epoch: number, secret: Uint8Array}>;
  data_secrets: Array<{_id: string, secret: Uint8Array}>;

  group_keys?: Array<Uint8Array>;

  externalPubKey?: string;
  externalPrivKey?: string;

  submissionPubKey?: string;
  submissionPriKey?: string;

  treenodes: Array<RatchetTreeNode>;

  userSet?: Set<string>;

  welcomed?: boolean;
  outOfSync?: boolean;
  unreadMessages?: boolean;
  unreadMentions?: boolean;
  lastMsgTime?: DateTime;

  static clone(rtree: RatchetTree | Group): RatchetTree {
    const newtree = new RatchetTree(rtree);
    if (rtree.treenodes) {
      for (let i = 0; i < newtree.treenodes.length; i++) {
        newtree.treenodes[i] = {...rtree.treenodes[i]};
      }
    }
    return newtree;
  }

  constructor (group: Group) {
    this._id = group._id;
    this.epoch = group.epoch;
    this.msgCounter = group.msgCounter;
    this.groupname = group.groupname;
    this.grouptype = group.grouptype;
    this.submissiontype = group.submissiontype || GroupSubmissionType.normal;
    this.desc = group.desc;
    this.accountID = group.accountID;
    this.archiving = group.archiving;
    this.created = moment.isMoment(group.created) ?
      group.created :
      typeof group.created === "string" ?  moment(group.created, moment.ISO_8601) :
      (group.created as any)._d ? moment((group.created as any)._d) :
      moment().subtract(30, "days");
    this.pki = group.pki || GroupPKI.p256;
    this.protocolScheme = group.protocolScheme || ProtocolScheme.mls10;
    this.protoVersion = group.protoVersion;
    this.userHash = group.userHash;

    this.sender_data_secret = group.sender_data_secret;
    this.exporter_secret = group.exporter_secret;
    this.authentication_secret = group.authentication_secret;
    //this.external_secret = group.external_secret;
    this.confirmation_key = group.confirmation_key;
    this.membership_key = group.membership_key;
    this.next_init_secret = group.next_init_secret;

    this.resumption_secrets = group.resumption_secrets ? [...group.resumption_secrets]: [];
    this.data_secrets = group.data_secrets ? Array.from(group.data_secrets) : [];
    this.group_keys = group.group_keys ? Array.from(group.group_keys) : undefined;
    this.externalPubKey = group.externalPubKey;
    this.externalPrivKey = group.externalPrivKey;
    this.submissionPubKey = group.submissionPubKey;
    this.submissionPriKey = group.submissionPriKey;

    this.treenodes = group.treenodes ? [...group.treenodes] : [];
    this.welcomed = group.welcomed;
    this.outOfSync = group.outOfSync;
    this.unreadMessages = group.unreadMessages;
    this.unreadMentions = group.unreadMentions;
    this.lastMsgTime = group.lastMsgTime;
    //this.treeHash = "";
    //this.confirmedTranscriptHash = "";
    //this.interimTranscriptHash = "";
  }

  /*get root(): RatchetTreeNode {
    return this.treenodes[this.rootIndex];
  }*/

  get rootIndex(): number {
    const len = this.treenodes.length;
    if (len <= 0) {
      throw new Error("RatchetTree is empty, looking for root");
    }
    //const neededNodeCount = 2*(len - 1) + 1;
    const index = (1 << Math.log2(len)) - 1;
    return index
  }

  /**
   * The level of a node in the tree. Leaves are level 0, their parents are
   * level 1, etc. If a node's children are at different levels, then its
   * level is the max level of its children plus one.
   */
  private level(nodeIndex: number): number {
    if ((nodeIndex & 0x01) === 0) { return 0; }
    let k = 0;
    while (((nodeIndex >> k) & 0x01) === 1) {
      k += 1
    }
    return k;
  }

  // could produce an index beyond nodes.length
  private parentIndexAttempt(nodeIndex: number) {
    const k = this.level(nodeIndex);
    const b = (nodeIndex >> (k + 1)) & 0x01;
    return (nodeIndex | (1 << k)) ^ (b << (k + 1));
  }

  private resolveParentIndex(nodeIndex: number): number {
    let p = this.parentIndexAttempt(nodeIndex);
    while (p >= this.treenodes.length) {
      p = this.parentIndexAttempt(p);
    }
    return p;
  }

  parentIndex(nodeIndex: number): number {
    if (nodeIndex == this.rootIndex) {
      return -1;
    }
    return this.resolveParentIndex(nodeIndex);
  }

  siblingIndex(nodeIndex: number): number {
    if (nodeIndex == this.rootIndex) {
      return -1;
    }
    const p = this.resolveParentIndex(nodeIndex);
    if (nodeIndex < p) {
      return this.rightIndex(p);
    }
    return this.leftIndex(p);
  }

  // index of left child of 'nodeIndex'
  leftIndex (nodeIndex: number): number {
    const k = this.level(nodeIndex);
    if (k === 0) { return -1; }
    return nodeIndex ^ (0x01 << (k - 1));
  }

  // index of right child of 'nodeIndex'
  rightIndex (nodeIndex: number): number {
    const k = this.level(nodeIndex);
    if (k === 0) { return -1; }
    let r = nodeIndex ^ (0x03 << (k - 1));
    while (r >= this.treenodes.length) {
      r = this.leftIndex(r);
    }
    return r
  }

  common_ancestor(xIndex: number, yIndex: number): number {
    const lx = this.level(xIndex);
    const ly = this.level(yIndex);
    if (lx <= ly && xIndex >> ly == yIndex >> ly) {
      return yIndex;
    } else if (ly <= lx && xIndex >> lx == yIndex >> lx) {
      return xIndex;
    }

    let k = 0;
    while (xIndex !== yIndex) {
      xIndex = xIndex >> 1;
      yIndex = yIndex >> 1;
      k += 1;
    }
    return (xIndex << k) + (1 << (k - 1)) - 1;
  }

  addLeaf (node: RatchetTreeNode): number {
    if (this.treenodes.length > 0) {
      this.treenodes.push({generation: 0, handshakeGeneration: 0});
    }
    this.treenodes.push(node);
    return this.treenodes.length - 1;
  }

  directPath (nodeIndex: number): TreePaths {
    const rootIndex = this.rootIndex;

    this.treenodes[nodeIndex].index = nodeIndex;
    const path: Array<RatchetTreeNode> = [this.treenodes[nodeIndex]];
    const copath: Array<RatchetTreeNode> = [];

    while (nodeIndex != rootIndex) {
      const siblingIndex = this.siblingIndex(nodeIndex);
      const sibling = this.treenodes[siblingIndex];
      sibling.index = siblingIndex;
      copath.push(sibling);
      nodeIndex = this.parentIndex(nodeIndex);
      this.treenodes[nodeIndex].index = nodeIndex;
      path.push(this.treenodes[nodeIndex]);
    }

    return {path, copath};
  }

  groupContext(contentType: string, epoch?: number): ArrayBuffer {
    //convert _id string to bytes
    let bufLen = 16; // 12 for ID, 4 for epoch
    const matches = this._id.match(/[\dA-F]{2}/gi);
    if (!matches) {
      throw new Error("Couldn't parse group ID for Group Context");
    }
    const idBytes = new Uint8Array(matches.map(s => parseInt(s, 16)));
    const epochVal = epoch === 0 ? 0 : (epoch || this.epoch);

    const encoder = new TextEncoder();
    const contentTypeBuf = encoder.encode(contentType);

    bufLen += contentTypeBuf.length;

    const buf = new ArrayBuffer(bufLen);
    const view = new Uint8Array(buf);
    view.set(idBytes);
    view.set(contentTypeBuf, idBytes.length + 4); // 12 ID bytes + 4 epoch bytes;
    new DataView(buf).setUint32(idBytes.length, epochVal, true);
    return buf;
  }

  hasUser(userID: string): boolean {
    if (!this.userSet) {
      this.userSet = new Set<string>();
      for (const n of this.treenodes) { n.userID && this.userSet.add(n.userID); }
    }
    return this.userSet.has(userID);
  }

  indexOfDevice(deviceID: string): number {
    return this.treenodes.findIndex(n => n.deviceID === deviceID);
  }

  resolvePrivateKeyIndex(nodeIndex: number): number {
    if (nodeIndex < 0) { return nodeIndex; }
    if (this.treenodes[nodeIndex].privateKey) {
      return nodeIndex;
    }
    const left = this.resolvePrivateKeyIndex(this.leftIndex(nodeIndex));
    if (left >= 0) { return left; }
    return this.resolvePrivateKeyIndex(this.rightIndex(nodeIndex))
  }

  resolvePublicKeyIndex(nodeIndex: number): number {
    if (nodeIndex < 0) { return nodeIndex; }
    if (this.treenodes[nodeIndex].publicKey) {
      return nodeIndex;
    }
    const left = this.resolvePublicKeyIndex(this.leftIndex(nodeIndex));
    if (left >= 0) { return left; }
    return this.resolvePublicKeyIndex(this.rightIndex(nodeIndex))
  }

  *iteratePublicKeys(nodeIndex: number): Generator<number, void> {
    if (nodeIndex < 0) { return; }
    if (this.treenodes[nodeIndex].publicKey) {
      yield nodeIndex;
      return;
    }
    yield * this.iteratePublicKeys(this.leftIndex(nodeIndex));
    yield * this.iteratePublicKeys(this.rightIndex(nodeIndex));
  }
}
